import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { doc, getDoc, collection, addDoc, serverTimestamp, query, where, getDocs, setDoc, Timestamp, deleteDoc, orderBy, limit } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';

// Create website audit via Firebase Cloud Function (direct DataForSEO integration)
export const createWebsiteAudit = async (url, userId, targetKeywords = []) => {
  console.log('🔧 createWebsiteAudit called with:', { url, userId, targetKeywords });

  if (!url || !userId) {
    throw new Error(`Missing required parameters: url=${url}, userId=${userId}`);
  }
  
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Get user's account ID from portal access service (most reliable)
    let accountId = null;
    try {
      const { getAccountData } = await import('./portalAccessService');
      const accountData = await getAccountData(userId);
      if (accountData && accountData.accountId) {
        accountId = accountData.accountId;
      }
    } catch (portalError) {
      console.warn('🔧 Could not fetch accountId from portal access:', portalError);
    }
    
    if (!accountId) {
      try {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          accountId = userDoc.data().accountId;
        }
      } catch (error) {
        console.warn('🔧 Could not fetch user accountId from user doc:', error);
      }
    }
    
    if (!accountId) {
      accountId = userId;
    }

    // Save audit in progress state to Firestore
    const auditInProgressId = `audit_progress_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const auditProgressData = {
      id: auditInProgressId,
      url: url,
      accountId: accountId,
      userId: userId,
      status: 'in_progress',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const progressRef = doc(db, 'users', userId, 'auditProgress', auditInProgressId);
    await setDoc(progressRef, auditProgressData);

    // Call Firebase Cloud Function to create DataForSEO audit task
    const createAudit = httpsCallable(functions, 'createWebsiteAudit');
    const result = await createAudit({ url, userId, targetKeywords });

    if (!result.data?.success) {
      throw new Error(result.data?.message || 'Failed to create audit task');
    }

    return {
      ...result.data,
      auditProgressId: auditInProgressId,
    };
  } catch (error) {
    console.error('Error creating website audit:', error);
    throw error;
  }
};

/**
 * Fetch the latest completed website audit for an account (assessment/CRM use).
 */
export const getLatestAccountAudit = async (accountId) => {
  if (!accountId) {
    return { success: true, audit: null };
  }

  try {
    const auditsQuery = query(
      collection(db, 'accounts', accountId, 'audits'),
      where('status', '==', 'completed'),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(auditsQuery);

    if (!snapshot.empty) {
      const docSnap = snapshot.docs[0];
      return {
        success: true,
        audit: { id: docSnap.id, ...docSnap.data() },
      };
    }

    return { success: true, audit: null };
  } catch (error) {
    if (error.code === 'failed-precondition' || error.message?.includes('index')) {
      const fallbackQuery = query(
        collection(db, 'accounts', accountId, 'audits'),
        where('status', '==', 'completed')
      );
      const snapshot = await getDocs(fallbackQuery);

      if (snapshot.empty) {
        return { success: true, audit: null };
      }

      const audits = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      audits.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
        const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
        return bTime - aTime;
      });

      return { success: true, audit: audits[0] };
    }

    throw error;
  }
};

/**
 * Trigger a website audit for a specific account (BBA staff or own account).
 * Does not write auditProgress — completion is tracked via accounts/{accountId}/audits.
 */
export const createAccountWebsiteAudit = async (url, accountId) => {
  if (!url || !accountId) {
    throw new Error('URL and accountId are required');
  }

  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const createAudit = httpsCallable(functions, 'createWebsiteAudit');
  const result = await createAudit({
    url,
    userId: currentUser.uid,
    accountId,
  });

  if (!result.data?.success) {
    throw new Error(result.data?.message || 'Failed to create audit task');
  }

  return result.data;
};

// Get audit status
export const getAuditStatus = async (taskId, userId) => {
  try {
    const getStatus = httpsCallable(functions, 'getAuditStatus');
    const result = await getStatus({ taskId, userId });
    return result.data;
  } catch (error) {
    console.error('Error getting audit status:', error);
    throw error;
  }
};

// Get user's audit history
export const getUserAudits = async (userId) => {
  try {
    // Get current user to ensure authentication
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    console.log('🔧 getUserAudits - Current user:', currentUser);
    console.log('🔧 getUserAudits - User UID:', currentUser?.uid);
    console.log('🔧 getUserAudits - User authenticated:', !!currentUser);
    
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    
    const getAudits = httpsCallable(functions, 'getUserAudits');
    const result = await getAudits({ userId });
    return result.data;
  } catch (error) {
    console.error('Error getting user audits:', error);
    throw error;
  }
};

// Get user's audit progress
export const getUserAuditProgress = async (userId) => {
  try {
    const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
    const progressRef = collection(db, 'users', userId, 'auditProgress');
    const q = query(progressRef, orderBy('createdAt', 'desc'), limit(1));
    
    const querySnapshot = await getDocs(q);
    const progressData = [];
    
    querySnapshot.forEach((doc) => {
      progressData.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return {
      success: true,
      progress: progressData.length > 0 ? progressData[0] : null
    };
  } catch (error) {
    console.error('Error getting audit progress:', error);
    throw error;
  }
};

// Clear audit progress
export const clearAuditProgress = async (userId, progressId) => {
  try {
    const { doc, deleteDoc } = await import('firebase/firestore');
    const progressRef = doc(db, 'users', userId, 'auditProgress', progressId);
    await deleteDoc(progressRef);
    
    console.log('🔧 Cleared audit progress:', progressId);
    return { success: true };
  } catch (error) {
    console.error('Error clearing audit progress:', error);
    throw error;
  }
};

// Cancel audit (clear all in-progress audits for user)
export const cancelAudit = async (userId) => {
  try {
    const { collection, getDocs, query, where, deleteDoc, doc } = await import('firebase/firestore');
    const progressRef = collection(db, 'users', userId, 'auditProgress');
    const q = query(progressRef, where('status', '==', 'in_progress'));
    
    const querySnapshot = await getDocs(q);
    const deletePromises = [];
    
    querySnapshot.forEach((docSnapshot) => {
      deletePromises.push(deleteDoc(doc(db, 'users', userId, 'auditProgress', docSnapshot.id)));
    });
    
    await Promise.all(deletePromises);
    
    console.log(`🔧 Cancelled ${querySnapshot.docs.length} audit(s) for user:`, userId);
    return { 
      success: true, 
      cancelledCount: querySnapshot.docs.length 
    };
  } catch (error) {
    console.error('Error cancelling audit:', error);
    throw error;
  }
};

// Poll audit status until completion
export const pollAuditStatus = async (taskId, userId, onProgress = null) => {
  const maxAttempts = 180; // 15 minutes with 5-second intervals
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const result = await getAuditStatus(taskId, userId);
      
      if (onProgress) {
        onProgress(result.status, attempts);
      }

      console.log(`🔍 Poll attempt ${attempts + 1}/${maxAttempts}: Status = ${result.status}`);

      if (result.status === 'Ok.' && result.results) {
        console.log('✅ Audit completed successfully!');
        return result.results;
      }

      // If status indicates an error, throw it
      if (result.status && result.status.includes('error')) {
        throw new Error(`Audit failed: ${result.status}`);
      }

      // Wait 5 seconds before next check
      await new Promise(resolve => setTimeout(resolve, 5000));
      attempts++;
    } catch (error) {
      console.error('Error polling audit status:', error);
      attempts++;
      
      // If it's a critical error, don't continue polling
      if (error.message.includes('Audit failed:')) {
        throw error;
      }
    }
  }

  throw new Error('Audit polling timeout - DataForSEO audits can take up to 15 minutes. Please check your audit history for results.');
};

// Transform audit results to dashboard format
export const transformAuditResults = (results) => {
  if (!results) return null;

  // Check if this is the new webhook format (has organicTraffic, backlinkProfile, etc.)
  if (results.organicTraffic && results.backlinkProfile) {
    // This is the new webhook format - return as-is with some enhancements
    return {
      lastScanned: results.lastScanned || new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      seoScore: results.seoScore ?? results.pageAudit?.onpageScore ?? 0,
      pageHealth: results.pageHealth ?? 0,
      contentScore: results.contentScore ?? 0,
      onPageScore: results.onPageScore ?? results.pageAudit?.onpageScore ?? 0,
      offPageScore: results.offPageScore || 0,
      technicalScore: results.technicalScore || 0,
      visibilityScore: results.visibilityScore || 0,
      organicTraffic: results.organicTraffic || {
        amount: 0,
        rankingKeywords: 0,
        estimatedTrafficValue: 0,
        keyInsight: 'No traffic data available'
      },
      backlinkProfile: results.backlinkProfile || {
        backlinks: 0,
        spamScore: 0,
        nofollowLinks: 0,
        keyInsight: 'No backlink data available'
      },
      historicalTraffic: results.historicalTraffic || {
        averageETV: 0,
        highestETV: 0,
        keywordsPerMonth: 0,
        keyInsight: 'No historical data available'
      },
      rankedKeywords: results.rankedKeywords || {
        total: 0,
        top10: 0,
        organicTrafficValue: 0,
        keyInsight: 'No keyword data available'
      },
      pageAudit: {
        onpageScore: results.pageAudit?.onpageScore ?? results.onPageScore ?? 0,
        loadSpeed: results.pageAudit?.loadSpeed ?? 0,
        errorWarnings: results.pageAudit?.errorWarnings ?? 0,
        keyInsight: results.pageAudit?.keyInsight || 'No page audit data available',
      },
      errorsWarnings: results.errorsWarnings || {
        criticalErrors: results.errorsWarnings?.criticalErrors ?? 0,
        warnings: 0,
        notices: 0,
        accessibilityIssues: 0,
        performanceIssues: 0,
        seoIssues: 0,
        keyInsight: 'No error data available'
      },
      // AI search optimization data (AI keyword volume + LLM mentions)
      aiSearch: results.aiSearch || null,
      // Generate some sample issues based on the data
      issues: generateIssuesFromData(results),
      // Generate some sample pages based on the data
      pages: generatePagesFromData(results),
      // Generate opportunities based on the data
      opportunities: generateOpportunitiesFromData(results)
    };
  }

  // Legacy DataForSEO format (keep for backward compatibility)
  const { summary, pages, issues, metrics } = results;

  // Transform issues for the dashboard
  const transformedIssues = issues ? issues.map(issue => ({
    issue: issue.message || issue.type,
    pagesAffected: issue.pagesAffected || 0,
    fixType: getFixTypeFromIssue(issue)
  })) : [];

  // Transform pages for the dashboard
  const transformedPages = pages ? pages.map(page => ({
    url: page.url,
    seoScore: calculatePageSEOScore(page),
    wordCount: page.wordCount || 0,
    metaIssues: countMetaIssues(page),
    speed: page.loadTime || 0
  })) : [];

  // Generate opportunities
  const opportunities = generateOpportunities(pages || [], issues || []);

  return {
    lastScanned: new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    seoScore: metrics?.seoScore || 0,
    pageHealth: metrics?.pageHealth || 0,
    contentScore: metrics?.contentScore || 0,
    aiSearch: results.aiSearch || null,
    issues: transformedIssues,
    pages: transformedPages,
    opportunities: opportunities
  };
};

// Helper function to determine fix type from issue
const getFixTypeFromIssue = (issue) => {
  const message = (issue.message || '').toLowerCase();
  const type = (issue.type || '').toLowerCase();

  if (message.includes('broken') || message.includes('404')) return 'Critical';
  if (message.includes('duplicate') || type.includes('duplicate')) return 'SEO';
  if (message.includes('slow') || message.includes('performance')) return 'Performance';
  if (message.includes('meta') || message.includes('title')) return 'SEO';
  if (message.includes('alt') || message.includes('image')) return 'Accessibility';
  if (message.includes('h1') || message.includes('heading')) return 'Structure';
  
  return 'SEO';
};

// Calculate individual page SEO score
const calculatePageSEOScore = (page) => {
  let score = 100;
  
  if (!page.title) score -= 20;
  if (!page.metaDescription) score -= 15;
  if (page.wordCount < 300) score -= 10;
  if (page.h1Count === 0) score -= 10;
  if (page.h1Count > 1) score -= 5;
  if (page.imagesWithoutAlt > 0) score -= 5;
  if (page.brokenLinks > 0) score -= 10;
  if (page.duplicateTitle) score -= 15;
  if (page.duplicateMetaDescription) score -= 10;
  if (page.loadTime > 3) score -= 10;
  
  return Math.max(0, score);
};

// Count meta issues for a page
const countMetaIssues = (page) => {
  let count = 0;
  
  if (!page.title) count++;
  if (!page.metaDescription) count++;
  if (page.duplicateTitle) count++;
  if (page.duplicateMetaDescription) count++;
  if (page.h1Count === 0) count++;
  if (page.h1Count > 1) count++;
  
  return count;
};

// Generate optimization opportunities
const generateOpportunities = (pages, issues) => {
  const opportunities = [];
  
  // Thin pages
  const thinPages = pages.filter(page => page.wordCount < 300);
  if (thinPages.length > 0) {
    opportunities.push({
      title: 'Thin Pages to Rewrite',
      content: 'List pages <300 words',
      count: thinPages.length
    });
  }
  
  // Slow pages
  const slowPages = pages.filter(page => page.loadTime > 3);
  if (slowPages.length > 0) {
    opportunities.push({
      title: 'Pages Slowing You Down',
      content: 'Page load time > 3s',
      count: slowPages.length
    });
  }
  
  // Missing meta descriptions
  const missingMeta = pages.filter(page => !page.metaDescription);
  if (missingMeta.length > 0) {
    opportunities.push({
      title: 'Missing Meta Descriptions',
      content: 'Pages without meta descriptions',
      count: missingMeta.length
    });
  }
  
  // Images without alt text
  const imagesWithoutAlt = pages.filter(page => page.imagesWithoutAlt > 0);
  if (imagesWithoutAlt.length > 0) {
    opportunities.push({
      title: 'Images Without Alt Text',
      content: 'Images missing alt attributes',
      count: imagesWithoutAlt.length
    });
  }
  
  return opportunities;
};

// Generate issues from webhook data
const generateIssuesFromData = (data) => {
  const issues = [];
  
  // Generate issues based on error/warning data
  if (data.errorsWarnings) {
    const { criticalErrors, warnings, notices, accessibilityIssues, performanceIssues, seoIssues } = data.errorsWarnings;
    
    if (criticalErrors > 0) {
      issues.push({
        issue: 'Critical Errors Detected',
        pagesAffected: criticalErrors,
        fixType: 'Critical'
      });
    }
    
    if (warnings > 0) {
      issues.push({
        issue: 'Website Warnings',
        pagesAffected: warnings,
        fixType: 'SEO'
      });
    }
    
    if (accessibilityIssues > 0) {
      issues.push({
        issue: 'Accessibility Issues',
        pagesAffected: accessibilityIssues,
        fixType: 'Accessibility'
      });
    }
    
    if (performanceIssues > 0) {
      issues.push({
        issue: 'Performance Issues',
        pagesAffected: performanceIssues,
        fixType: 'Performance'
      });
    }
    
    if (seoIssues > 0) {
      issues.push({
        issue: 'SEO Issues',
        pagesAffected: seoIssues,
        fixType: 'SEO'
      });
    }
  }
  
  // Generate issues based on scores
  if (data.seoScore < 60) {
    issues.push({
      issue: 'Low SEO Score',
      pagesAffected: 1,
      fixType: 'SEO'
    });
  }
  
  if (data.pageHealth < 60) {
    issues.push({
      issue: 'Poor Page Health',
      pagesAffected: 1,
      fixType: 'Performance'
    });
  }
  
  if (data.backlinkProfile?.spamScore > 20) {
    issues.push({
      issue: 'High Spam Score',
      pagesAffected: 1,
      fixType: 'Critical'
    });
  }
  
  return issues;
};

// Generate sample pages from webhook data
const generatePagesFromData = (data) => {
  const pages = [];
  
  // Generate sample pages based on the data
  if (data.rankedKeywords?.total > 0) {
    pages.push({
      url: '/homepage',
      seoScore: data.seoScore || 0,
      wordCount: Math.floor(Math.random() * 1000) + 200,
      metaIssues: data.errorsWarnings?.seoIssues || 0,
      speed: data.pageAudit?.loadSpeed || 0
    });
  }
  
  if (data.rankedKeywords?.total > 1) {
    pages.push({
      url: '/about',
      seoScore: Math.max(0, (data.seoScore || 0) - 10),
      wordCount: Math.floor(Math.random() * 800) + 150,
      metaIssues: Math.max(0, (data.errorsWarnings?.seoIssues || 0) - 1),
      speed: (data.pageAudit?.loadSpeed || 0) + 0.5
    });
  }
  
  if (data.rankedKeywords?.total > 2) {
    pages.push({
      url: '/services',
      seoScore: Math.max(0, (data.seoScore || 0) - 5),
      wordCount: Math.floor(Math.random() * 600) + 100,
      metaIssues: Math.max(0, (data.errorsWarnings?.seoIssues || 0) - 2),
      speed: (data.pageAudit?.loadSpeed || 0) + 0.3
    });
  }
  
  return pages;
};

// Generate opportunities from webhook data
const generateOpportunitiesFromData = (data) => {
  const opportunities = [];
  
  // SEO opportunities
  if (data.seoScore < 70) {
    opportunities.push({
      title: 'Improve SEO Score',
      content: `Current score: ${data.seoScore}/100`,
      count: 1
    });
  }
  
  // Performance opportunities
  if (data.pageAudit?.loadSpeed > 3) {
    opportunities.push({
      title: 'Optimize Page Speed',
      content: `Current load time: ${data.pageAudit.loadSpeed}s`,
      count: 1
    });
  }
  
  // Backlink opportunities
  if (data.backlinkProfile?.backlinks < 50) {
    opportunities.push({
      title: 'Build More Backlinks',
      content: `Current backlinks: ${data.backlinkProfile.backlinks}`,
      count: 1
    });
  }
  
  // Keyword opportunities
  if (data.rankedKeywords?.top10 < 10) {
    opportunities.push({
      title: 'Improve Keyword Rankings',
      content: `Keywords in top 10: ${data.rankedKeywords.top10}`,
      count: 1
    });
  }
  
  // Error fixing opportunities
  if (data.errorsWarnings?.warnings > 0) {
    opportunities.push({
      title: 'Fix Website Warnings',
      content: `Total warnings: ${data.errorsWarnings.warnings}`,
      count: data.errorsWarnings.warnings
    });
  }
  
  return opportunities;
};

/**
 * Save website management request to database
 * @param {string} userId - The user's UID
 * @param {Object} managementRequestData - Management request data object
 * @returns {Promise<Object>} Created request object
 */
export const saveWebsiteManagementRequest = async (userId, managementRequestData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get user data for display name, email, and company
    const userDoc = await getDoc(doc(db, 'users', userId));
    let requestedByName = 'Unknown User';
    let userEmail = 'unknown@example.com';
    let companyName = 'Unknown Company';
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const fullName = userData.displayName || 
                      (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                      userData.email || 
                      'Unknown User';
      
      companyName = userData.company || 'Unknown Company';
      
      // Format as "Name" with "Company" underneath
      requestedByName = `${fullName}\n${companyName}`;
      userEmail = userData.email || 'unknown@example.com';
    } else {
      // If userId doesn't work, try to find user by email from auth
      const auth = getAuth();
      if (auth.currentUser && auth.currentUser.email) {
        try {
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', auth.currentUser.email)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userData = usersSnapshot.docs[0].data();
            const fullName = userData.displayName || 
                            (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                            userData.email || 
                            'Unknown User';
            
            companyName = userData.company || 'Unknown Company';
            
            // Format as "Name" with "Company" underneath
            requestedByName = `${fullName}\n${companyName}`;
            userEmail = userData.email || 'unknown@example.com';
          }
        } catch (error) {
          console.error('Error finding user by email:', error);
        }
      }
    }

    // Get current website data (if any exists)
    const currentData = await getUserWebsiteData(userId);
    
    // Create management request object for website data
    const managementRequest = {
      selectedServices: Array.isArray(managementRequestData.selectedServices) ? managementRequestData.selectedServices : [],
      websiteUrl: managementRequestData.websiteUrl || '',
      monthlyBudget: managementRequestData.monthlyBudget || '',
      priorityLevel: managementRequestData.priorityLevel || '',
      submittedAt: new Date().toISOString(),
      status: 'requested'
    };

    // Create management request object for marketingRequests collection
    const marketingRequest = {
      userId: userId,
      requestedByName: requestedByName,
      userEmail: userEmail,
      title: 'Website Management Request',
      description: `Website management request for ${managementRequestData.websiteUrl || 'website'}`,
      category: 'website',
      priority: (() => {
        const level = managementRequestData.priorityLevel || 'normal';
        const mapping = {
          'Low': 'low',
          'Medium': 'normal', 
          'High': 'high',
          'Urgent': 'critical'
        };
        return mapping[level] || level.toLowerCase();
      })(),
      budget: managementRequestData.monthlyBudget ? `$${managementRequestData.monthlyBudget}/month` : 'Not specified',
      status: 'requested',
      selectedServices: managementRequestData.selectedServices || [],
      websiteUrl: managementRequestData.websiteUrl || '',
      monthlyBudget: managementRequestData.monthlyBudget || '',
      priorityLevel: managementRequestData.priorityLevel || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Update the website data with management request
    const updatedData = {
      ...currentData,
      managementRequest: managementRequest
    };

    // Save to website database (create a website collection similar to social media)
    const userDocRef = doc(db, 'websiteData', userId);
    await setDoc(userDocRef, {
      managementRequest: managementRequest,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Save to marketingRequests collection
    const requestsRef = collection(db, 'marketingRequests');
    const docRef = await addDoc(requestsRef, marketingRequest);
    
    console.log('✅ Website management request saved:', docRef.id);

    // Trigger marketing fulfillment notification for website management request
    try {
      const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
      await triggerFulfillmentNotification('marketing', {
        type: 'website management',
        name: 'Website Management Request',
        description: `Website management request for ${managementRequestData.websiteUrl || 'website'}`,
        requestedBy: requestedByName,
        company: 'User Company', // You might want to get this from user data
        websiteUrl: managementRequestData.websiteUrl || '',
        selectedServices: managementRequestData.selectedServices || [],
        monthlyBudget: managementRequestData.monthlyBudget || '',
        priorityLevel: managementRequestData.priorityLevel || '',
        timestamp: new Date().toISOString()
      });
      console.log('✅ Marketing fulfillment notification sent for website management request');
    } catch (notificationError) {
      console.error('❌ Error sending marketing fulfillment notification:', notificationError);
      // Don't fail the request if notification fails
    }

    return updatedData;
  } catch (error) {
    console.error('Error saving website management request:', error);
    throw error;
  }
};

/**
 * Get user website data including management request
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} User website data
 */
export const getUserWebsiteData = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only access their own data');
    }

    const userDocRef = doc(db, 'websiteData', userId);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      return {
        managementRequest: null,
        createdAt: null
      };
    }
  } catch (error) {
    console.error('Error getting user website data:', error);
    throw error;
  }
};

/**
 * Get website management request status from marketingRequests collection
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Marketing request data with status
 */
export const getWebsiteManagementRequestStatus = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only access their own data');
    }

    // Query marketingRequests collection for website requests by this user (without orderBy to avoid index requirement)
    const requestsQuery = query(
      collection(db, 'marketingRequests'),
      where('userId', '==', userId),
      where('category', '==', 'website')
    );
    
    const querySnapshot = await getDocs(requestsQuery);
    
    if (!querySnapshot.empty) {
      // Get the most recent document by sorting in JavaScript
      const docs = querySnapshot.docs;
      const sortedDocs = docs.sort((a, b) => {
        const aTime = a.data().createdAt?.toDate?.() || new Date(a.data().createdAt || 0);
        const bTime = b.data().createdAt?.toDate?.() || new Date(b.data().createdAt || 0);
        return bTime - aTime; // Most recent first
      });
      
      const doc = sortedDocs[0];
      return {
        success: true,
        request: {
          id: doc.id,
          ...doc.data()
        }
      };
    } else {
      return {
        success: true,
        request: null
      };
    }
  } catch (error) {
    console.error('Error getting website management request status:', error);
    throw error;
  }
};

/**
 * Cancel website management request for a specific user
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Updated website data
 */
export const cancelWebsiteManagementRequest = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserWebsiteData(userId);
    
    // Remove management request
    const updatedData = {
      ...currentData,
      managementRequest: null
    };

    // Save to website database
    const userDocRef = doc(db, 'websiteData', userId);
    await setDoc(userDocRef, {
      managementRequest: null,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Remove from marketingRequests collection
    try {
      const requestsQuery = query(
        collection(db, 'marketingRequests'),
        where('userId', '==', userId),
        where('category', '==', 'website'),
        where('status', '==', 'requested')
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      
      // Delete all requested website requests for this user
      const deletePromises = requestsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log('✅ Cancelled website management requests:', requestsSnapshot.docs.length);
    } catch (error) {
      console.error('Error removing from marketingRequests:', error);
      // Don't throw here, as the main operation succeeded
    }

    return updatedData;
  } catch (error) {
    console.error('Error canceling website management request:', error);
    throw error;
  }
}; 